/**
 * Shared source of truth for backend datapoint string codes.
 *
 * Every consumer (useDatapoints composable, company-detail store, and any future
 * composable/store that needs datapoint gating) MUST import from this module instead
 * of declaring its own local constants.
 *
 * Keep this list in sync with the backend DataPointLabels enum. Additions MUST use
 * SCREAMING_SNAKE_CASE keys with the exact backend string literal as the value.
 */
export const DATAPOINT_CODES = {
  // Business Location
  ENABLE_PHYSICAL_LOCATION_FILTER: 'ENABLE_PHYSICAL_LOCATION_FILTER',
  ENABLE_INTERNATIONAL_OFFICES: 'ENABLE_INTERNATIONAL_OFFICES',
  ENABLE_BRAND_FILTERS: 'ENABLE_BRAND_FILTERS',
  IS_LOCATOR_ENABLED: 'LOCATOR',

  // Industry
  INDUSTRY_DATAPOINT: 'INDUSTRY',
  SUB_INDUSTRY_DATAPOINT: 'SUBINDUSTRY',
  ENABLE_SIC_FILTER: 'ENABLE_SIC_FILTER',
  ENABLE_NAICS_FILTER: 'ENABLE_NAICS_FILTER',

  // Contact
  ENABLE_DIRECT_DIAL_MOBILE_FILTER: 'ENABLE_DIRECT_DIAL_MOBILE_FILTER',
  ENABLE_CONTACT_EMAIL_STATUS_FILTER: 'ENABLE_CONTACT_EMAIL_STATUS_FILTER',

  // Business Sizing
  ENABLE_BUSINESS_SIZING_FILTERS: 'ENABLE_BUSINESS_SIZING_FILTERS',
  ENABLE_GLOBALTRAFFIC: 'ENABLE_GLOBALTRAFFIC',

  // E-commerce
  ENABLE_E_COMMERCE_SEARCH: 'ENABLE_E_COMMERCE_SEARCH',
  DISABLE_WEB_SITE_TECHNOLOGIES: 'DISABLE_WEB_SITE_TECHNOLOGIES',
  DISABLE_FOREIGN_EXCHANGE: 'DISABLE_FOREIGN_EXCHANGE',
  SHIPPING_DATAPOINT: 'SHIPPING',
  ENABLE_INTERNATIONAL_SHIPPING: 'ENABLE_INTERNATIONAL_SHIPPING',
  MARKETPLACE_SELLER_FILTER: 'MARKETPLACE_SELLER_FILTER',

  // Technologies
  CLOUD_HOSTING_FILTER: 'CLOUD_HOSTING_FILTER',
  EMAIL_HOSTING_FILTER: 'EMAIL_HOSTING_FILTER',

  // Service Providers
  PAYMENTS_DATAPOINT: 'PAYMENT',
  ENABLE_IMPORTERS_AND_EXPORTERS: 'ENABLE_IMPORTERS_AND_EXPORTERS',

  // Advanced Search
  KEYWORD_SEARCH: 'KEYWORD_SEARCH',
  ENABLE_AUTHORIZED_PROVIDER: 'ENABLE_AUTHORIZED_PROVIDER',
  BUSINESS_MODEL_FILTER: 'BUSINESS_MODEL_FILTER',
  ENABLE_COMPANIES_WITH_LOCALIZED_SITES: 'ENABLE_COMPANIES_WITH_LOCALIZED_SITES',
  DISABLE_SHOW_LOCALIZED_DOMAINS: 'DISABLE_SHOW_LOCALIZED_DOMAINS',
  DISABLE_LEADLIST_FILTERS: 'DISABLE_LEADLIST_FILTERS',
  ENABLE_CRM_DATA_FILTER: 'ENABLE_CRM_DATA_FILTER',

  // Social Media
  ENABLE_SOCIAL_MEDIA_FILTERS: 'ENABLE_SOCIAL_MEDIA_FILTERS',

  // CRM
  ENABLE_VERIFY_CRM_DATA: 'ENABLE_VERIFY_CRM_DATA',
  DISABLE_CRM_EXPORT: 'DISABLE_CRM_EXPORT',
  ENABLE_CRM_DEDUP_OPTION: 'ENABLE_CRM_DEDUP_OPTION',
  ENABLE_INDUSTRY_MAPPING: 'ENABLE_INDUSTRY_MAPPING',
  SALESFORCE_REFRESH_COMPANIES: 'SALESFORCE_REFRESH_COMPANIES',
  SALESFORCE_REFRESH_CONTACTS: 'SALESFORCE_REFRESH_CONTACTS',
  SUPPRESS_CONTACT_DATA: 'SUPPRESS_CONTACT_DATA',
  ENABLE_HYBRID_EXPORT: 'ENABLE_HYBRID_EXPORT',

  // UI version gating
  ENABLE_VUE3_NEW_UI: 'ENABLE_VUE3_NEW_UI',

  // Lead Enrichment
  DEVELOPMENT_TEAM: 'DEVELOPMENT_TEAM',
  ENABLE_LEAD_ENRICHMENT: 'ENABLE_LEAD_ENRICHMENT',
  ENABLE_LEAD_SCORING: 'ENABLE_LEAD_SCORING',

  // AI Search
  ENABLE_AI_SEARCH: 'ENABLE_AI_SEARCH',

  // Navigation Gating
  ENABLE_LOOK_A_LIKE_SERVICE: 'ENABLE_LOOK_A_LIKE_SERVICE',
  HIDE_PEOPLE_SEARCH: 'HIDE_PEOPLE_SEARCH',
  ENABLE_PREFERRED_TITLES: 'ENABLE_PREFERRED_TITLES',
  ENABLE_EXTERNAL_SOFO_API: 'ENABLE_EXTERNAL_SOFO_API',
  BACK_OFFICE_INDEX: 'BACK_OFFICE_INDEX',

  // AI Assistant
  ENABLE_AI_ASSISTANT: 'ENABLE_AI_ASSISTANT',

  // Export control
  DISABLE_FILE_EXPORT: 'DISABLE_FILE_EXPORT',
  REQUIRE_CRM_CONNECTION: 'REQUIRE_CRM_CONNECTION',
  REMOVE_CONTACTS_WITH_NO_EMAIL_ON_EXPORT: 'REMOVE_CONTACTS_WITH_NO_EMAIL_ON_EXPORT',

  // NEW — the 19 missing flags actively used in Vue 2 (§4.1 of the source plan)
  DISPLAY_CONTACT_COUNT: 'DISPLAY_CONTACT_COUNT',
  BULKDOWNLOAD_REFRESH_CONTACTS: 'BULKDOWNLOAD_REFRESH_CONTACTS',
  SALESFORCE_DEDUP: 'SALESFORCE_DEDUP',
  DISABLE_PAGINATION: 'DISABLE_PAGINATION',
  DISABLE_COMPANY_COUNT: 'DISABLE_COMPANY_COUNT',
  COMPANY_NAME_SEARCH: 'COMPANY_NAME_SEARCH',
  ALLOW_COPY_CUT: 'ALLOW_COPY_CUT',
  ENABLE_MAX_LIMIT_EXPORT: 'ENABLE_MAX_LIMIT_EXPORT',
  ENABLE_NEW_MERCHANT_DATA: 'ENABLE_NEW_MERCHANT_DATA',
  ENABLE_REFRESH_CONTACT_ORCHESTRATOR: 'ENABLE_REFRESH_CONTACT_ORCHESTRATOR',
  ENABLE_REFRESH_CONTACT_LIST: 'ENABLE_REFRESH_CONTACT_LIST',
  ENABLE_CONTACT_LIST_SORT_BY: 'ENABLE_CONTACT_LIST_SORT_BY',
  DISABLE_MAX_LIMIT_PER_LIST: 'DISABLE_MAX_LIMIT_PER_LIST',
  ENABLE_REFRESH_CONTACT_REAL_TIME: 'ENABLE_REFRESH_CONTACT_REAL_TIME',
  ENABLE_DPD_CUSTOM_FILTER: 'ENABLE_DPD_CUSTOM_FILTER',
  DISABLE_DUPLICATE_CREATION: 'DISABLE_DUPLICATE_CREATION',
  ENABLE_EMPLOYEE_HISTORY: 'ENABLE_EMPLOYEE_HISTORY',
  DISABLE_MULTICOMPANIES_CRM_EXPORT: 'DISABLE_MULTICOMPANIES_CRM_EXPORT',
  COUNTRY_RESTRICTION: 'COUNTRY_RESTRICTION',

  // NEW — dropdown helper flags (§9)
  ENABLE_ADULT_CONTENT: 'ENABLE_ADULT_CONTENT',
  SHIPPING_COUNTRY_RESTRICTION: 'SHIPPING_COUNTRY_RESTRICTION',

  // NEW — graduated from company-detail.store.ts local constants (§11)
  ENABLE_SIMILAR_SITES: 'ENABLE_SIMILAR_SITES',

  // Gates the configurable "Customize columns" picker on the search results tables.
  ENABLE_SEARCH_RESULTS_COLUMNS: 'ENABLE_SEARCH_RESULTS_COLUMNS',

  // Shows the "Find more contacts" button on the company Contacts tab (on-demand contact acquisition).
  ENABLE_CONTACT_ACQUISITION_ON_DEMAND: 'ENABLE_CONTACT_ACQUISITION_ON_DEMAND',
  // Gates the "View People" + "Manual Refresh" buttons on the Company Detail Contacts tab
  // (Vue 2 datapoints.IsDisplayManualJobOnContactScreen).
  DISPLAY_MANUAL_JOB_ON_CONTACT_SCREEN: 'DISPLAY_MANUAL_JOB_ON_CONTACT_SCREEN',
} as const

export type DatapointCode = (typeof DATAPOINT_CODES)[keyof typeof DATAPOINT_CODES]

export const COMPANY_SIMPLE_URL_FAKE_PREFIX = '$'

export function hasNoSite(url?: string | null): boolean {
  return !url || url.startsWith(COMPANY_SIMPLE_URL_FAKE_PREFIX)
}
